/* Standalone PDP/collection prices for Faber coffee pods and accessories. */
(() => {
  const SELECTOR = '[data-rpd-standalone-price], [data-rpd-machine-card-price], [data-rpd-machine-card-badge]';
  const ENDPOINT = '/apps/forus-promotions/advanced-discounts';
  let configPromise = null;
  let refreshTimer = 0;

  const clean = (value) => String(value || '').trim().toLowerCase();

  const NOVEMBER_PREVIEW_TOKENS = {
    'faber:pro-deluxe-slot-plast': { percentage: 10, contexts: { upgrade: 12.5, 'upgrade-pods': 15, full: 20 } },
    'faber:slot-inox': { percentage: 5, contexts: { upgrade: 7.5, 'upgrade-pods': 8.5, full: 10 } },
    'faber:gamma-3': { percentage: 3, contexts: { upgrade: 5, 'upgrade-pods': 5, full: 5 } },
    'faber:coffee': { percentage: 20, contexts: { bundle: 30 } },
    'faber:accessory': { percentage: 10, contexts: { bundle: 20 } },
  };

  function isThemePreview() {
    return Boolean(
      document.getElementById('PBarNextFrame') ||
      document.documentElement.classList.contains('shopify-design-mode') ||
      window.Shopify?.designMode
    );
  }

  async function previewFallback(tokens) {
    if (Object.keys(tokens || {}).length) return tokens;
    for (let attempt = 0; attempt < 20; attempt += 1) {
      if (isThemePreview()) return NOVEMBER_PREVIEW_TOKENS;
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
    return {};
  }

  function loadTokens() {
    if (!configPromise) {
      configPromise = fetch(ENDPOINT, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Standalone pricing HTTP ${response.status}`);
          return response.json();
        })
        .then((payload) => previewFallback(payload?.tokens || {}))
        .catch((error) => {
          console.warn('[RPD Faber] Standalone pricing unavailable', error);
          configPromise = null;
          return {};
        });
    }

    return configPromise;
  }

  function configuredRate(tokens, token, context) {
    const config = tokens?.[clean(token)];
    if (!config) return 0;

    const contextual = Number(config?.contexts?.[clean(context)]);
    if (Number.isFinite(contextual) && contextual > 0) return contextual;

    const base = Number(config?.percentage);
    return Number.isFinite(base) && base > 0 ? base : 0;
  }

  function percentText(value) {
    const rounded = Math.round((Number(value) || 0) * 10) / 10;
    const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
    return (document.documentElement.lang || '').toLowerCase().startsWith('it')
      ? text.replace('.', ',')
      : text;
  }

  function money(cents, currency) {
    const locale = (document.documentElement.lang || 'it-IT').replace('_', '-');
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `€${((Number(cents) || 0) / 100).toFixed(2).replace('.', ',')}`;
    }
  }

  function renderPrice(box, tokens) {
    const baseCents = Number(box.dataset.rpdBaseCents || 0);
    const rate = configuredRate(tokens, box.dataset.rpdToken, box.dataset.rpdContext || 'standalone');
    const badgeOnly = box.matches('[data-rpd-machine-card-badge]');
    const originalWrap = box.querySelector('[data-rpd-standalone-original-wrap]');
    const original = box.querySelector('[data-rpd-standalone-original]');
    const final = box.querySelector('[data-rpd-standalone-final]');
    const badge = badgeOnly ? box : box.querySelector('[data-rpd-standalone-badge]');

    if (!rate || (!badgeOnly && !baseCents)) {
      if (originalWrap) originalWrap.hidden = true;
      if (final && baseCents) final.textContent = money(baseCents, box.dataset.rpdCurrency);
      if (badge) badge.hidden = true;
      return;
    }

    if (badgeOnly) {
      const prefix = box.dataset.rpdBadgePrefix || '';
      const suffix = box.dataset.rpdBadgeSuffix || '';
      badge.textContent = `${prefix}-${percentText(rate)}%${suffix}`;
      badge.hidden = false;
      return;
    }

    const finalCents = Math.round(baseCents * (1 - rate / 100));
    if (original) original.textContent = money(baseCents, box.dataset.rpdCurrency);
    if (originalWrap) originalWrap.hidden = false;
    if (final) final.textContent = money(finalCents, box.dataset.rpdCurrency);
    if (badge) {
      const prefix = box.dataset.rpdBadgePrefix || '';
      const suffix = box.dataset.rpdBadgeSuffix || '';
      badge.textContent = `${prefix}-${percentText(rate)}%${suffix}`;
      badge.hidden = false;
    }
    box.dataset.rpdStandaloneRate = String(rate);
  }

  async function refresh(scope = document) {
    const boxes = Array.from(scope.querySelectorAll?.(SELECTOR) || []);
    if (scope instanceof Element && scope.matches(SELECTOR)) boxes.unshift(scope);
    if (!boxes.length) return;

    const tokens = await loadTokens();
    boxes.forEach((box) => renderPrice(box, tokens));
  }

  function scheduleRefresh(delay = 0) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => refresh(document), delay);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleRefresh());
  } else {
    scheduleRefresh();
  }

  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('variant:update', () => scheduleRefresh(80));
  document.addEventListener('filter:update', () => scheduleRefresh(150));
})();
